import React, { useState, useEffect, useMemo } from 'react';
import styles from './StoreForm.module.scss';
import DatePicker from './DatePicker';
import { 
    calculateWithMixedDigits, 
    calculateTotal,
    getDigitType
} from '../../utils/digitConverter';
import API_BASE_URL from '../../config/api';
import { useAuth } from '../../context/AuthContext';

const StoreForm = ({ initialData, onSubmit, isEditMode = false }) => {
    // Compute formData using useMemo to ensure it updates when initialData changes
    const formData = useMemo(() => {
        if (initialData && isEditMode) {
            const computedFormData = {
                miti: initialData.miti || '',
                miti_nepali: initialData.miti_nepali || '',
                dakshila_no: initialData.dakshila_no || '',
                specification: initialData.specification || '',
                saman_ko_pahichan: initialData.saman_ko_pahichan || '',
                model_no: initialData.model_no || '',
                utpadan_garnedesh: initialData.utpadan_garnedesh || '',
                size: initialData.size || '',
                anumit_aayu: initialData.anumit_aayu || '',
                saman_prabidhik_srot: initialData.saman_prabidhik_srot || '',
                parikshan: initialData.parikshan || '',
                prati_ekai_ko_dar: initialData.prati_ekai_ko_dar ? String(initialData.prati_ekai_ko_dar) : '',
                jamma_paral_mulya: initialData.jamma_paral_mulya ? String(initialData.jamma_paral_mulya) : '',
                pariman: initialData.pariman ? String(initialData.pariman) : '',
                jamma_paral_mulya2: initialData.jamma_paral_mulya2 ? String(initialData.jamma_paral_mulya2) : '',
                pariman2: initialData.pariman2 ? String(initialData.pariman2) : '',
                jamma_paral_mulya3: initialData.jamma_paral_mulya3 ? String(initialData.jamma_paral_mulya3) : '',
                kaifiyat: initialData.kaifiyat || '',
                // New header fields
                form_number: initialData.form_number || '',
                previous_form_number: initialData.previous_form_number || '४७',
                fiscal_year: initialData.fiscal_year || '',
                inventory_code: initialData.inventory_code || '',
                inventory_page_number: initialData.inventory_page_number ? String(initialData.inventory_page_number) : '',
                item_name: initialData.item_name || '',
                unit_value: initialData.unit_value || ''
            };
            console.log('Edit mode - computed formData:', computedFormData);
            return computedFormData;
        }
        
        // Default form data for new items
        const defaultFormData = {
            miti: '',
            miti_nepali: '',
            dakshila_no: '',
            specification: '',
            saman_ko_pahichan: '',
            model_no: '',
            utpadan_garnedesh: '',
            size: '',
            anumit_aayu: '',
            saman_prabidhik_srot: '',
            parikshan: '',
            prati_ekai_ko_dar: '',
            jamma_paral_mulya: '',
            pariman: '',
            jamma_paral_mulya2: '',
            pariman2: '',
            jamma_paral_mulya3: '',
            kaifiyat: '',
            // New header fields
            form_number: '',
            previous_form_number: '',
            fiscal_year: '',
            inventory_code: '',
            inventory_page_number: '',
            item_name: '',
            unit_value: ''
        };
        return defaultFormData;
    }, [initialData, isEditMode]);

    const [localFormData, setLocalFormData] = useState(formData);
    const [showNepaliCalendar, setShowNepaliCalendar] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);
    const [hasUnsavedData, setHasUnsavedData] = useState(false);
    const [uploadStatus, setUploadStatus] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const { makeAuthenticatedRequest } = useAuth();

    // Synchronize localFormData with computed formData
    useEffect(() => {
        setLocalFormData(formData);
    }, [formData]);

    // Close calendar when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (showNepaliCalendar && !event.target.closest('.calendar-popup') && !event.target.closest('.calendar-btn')) {
                setShowNepaliCalendar(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showNepaliCalendar]);

    // Local Storage Functions
    const saveToLocalStorage = (data) => {
        try {
            localStorage.setItem('storeFormData', JSON.stringify(data));
            localStorage.setItem('storeFormLastSaved', new Date().toISOString());
            setLastSaved(new Date());
            setHasUnsavedData(false);
        } catch (error) {
            console.error('Error saving to localStorage:', error);
        }
    };

    const loadFromLocalStorage = () => {
        try {
            const savedData = localStorage.getItem('storeFormData');
            const lastSavedTime = localStorage.getItem('storeFormLastSaved');
            
            if (savedData) {
                const parsedData = JSON.parse(savedData);
                setLocalFormData(parsedData);
                setLastSaved(lastSavedTime ? new Date(lastSavedTime) : null);
                setHasUnsavedData(false);
                return true; // Indicates data was restored
            }
            return false; // No data to restore
        } catch (error) {
            console.error('Error loading from localStorage:', error);
            return false;
        }
    };

    const clearLocalStorage = () => {
        try {
            localStorage.removeItem('storeFormData');
            localStorage.removeItem('storeFormLastSaved');
            setLastSaved(null);
            setHasUnsavedData(false);
        } catch (error) {
            console.error('Error clearing localStorage:', error);
        }
    };

    // Handle restore button click
    const handleRestoreClick = () => {
        const hasSavedData = localStorage.getItem('storeFormData');
        if (hasSavedData) {
            const confirmed = window.confirm('Do you want to restore previously saved data? This will replace your current form data.');
            if (confirmed) {
                const restored = loadFromLocalStorage();
                if (restored) {
                    alert('Previous form data has been restored!');
                }
            }
        } else {
            alert('No saved data found to restore.');
        }
    };

    // Check if there's saved data available for restore
    const hasSavedDataAvailable = () => {
        try {
            const savedData = localStorage.getItem('storeFormData');
            return !!savedData;
        } catch (error) {
            return false;
        }
    };

    // Auto-save effect (only when not in edit mode)
    useEffect(() => {
        if (!isEditMode) {
            // Check if user has entered meaningful data (excluding default values)
            const defaultValues = {
                form_number: '',
                previous_form_number: '',
                fiscal_year: '',
                inventory_code: '',
                inventory_page_number: '',
                item_name: '',
                unit_value: ''
            };
            
            const hasUserData = Object.entries(localFormData).some(([key, value]) => {
                // Skip default values
                if (defaultValues[key] === value) return false;
                // Check if field has meaningful data
                return value && value.trim() !== '';
            });
            
            if (hasUserData) {
                setHasUnsavedData(true);
                const timeoutId = setTimeout(() => {
                    saveToLocalStorage(localFormData);
                }, 2000); // Auto-save after 2 seconds of inactivity

                return () => clearTimeout(timeoutId);
            } else {
                setHasUnsavedData(false);
            }
        }
    }, [localFormData, isEditMode]);

    // Note: Removed automatic localStorage restoration - now only manual via "Restore Data" button

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'miti_nepali') {
            // Auto-convert Nepali date to English when typing
            const englishDate = convertNepaliToEnglish(value);
            setLocalFormData(prev => ({
                ...prev,
                miti_nepali: value,
                miti: englishDate
            }));
        } else if (name === 'parikshan' || name === 'pariman' || name === 'pariman2') {
            // Handle quantity fields with mixed digit support
            setLocalFormData(prev => {
                const updatedData = {
                    ...prev,
                    [name]: value // Keep original input for display
                };
                
                // Auto-calculate totals if both quantity and rate are available
                if (name === 'parikshan' && prev.prati_ekai_ko_dar && value) {
                    const total = calculateTotal(value, prev.prati_ekai_ko_dar, getDigitType(value));
                    updatedData.jamma_paral_mulya = total;
                } else if (name === 'pariman' && prev.prati_ekai_ko_dar && value) {
                    const total = calculateTotal(value, prev.prati_ekai_ko_dar, getDigitType(value));
                    updatedData.jamma_paral_mulya2 = total;
                } else if (name === 'pariman2' && prev.prati_ekai_ko_dar && value) {
                    const total = calculateTotal(value, prev.prati_ekai_ko_dar, getDigitType(value));
                    updatedData.jamma_paral_mulya3 = total;
                }
                
                return updatedData;
            });
        } else if (name === 'prati_ekai_ko_dar') {
            // Handle rate field with mixed digit support
            setLocalFormData(prev => {
                const updatedData = {
                    ...prev,
                    [name]: value // Keep original input for display
                };
                
                // Auto-calculate totals for all quantity fields
                if (prev.parikshan && value) {
                    const total = calculateTotal(prev.parikshan, value, getDigitType(prev.parikshan));
                    updatedData.jamma_paral_mulya = total;
                }
                if (prev.pariman && value) {
                    const total = calculateTotal(prev.pariman, value, getDigitType(prev.pariman));
                    updatedData.jamma_paral_mulya2 = total;
                }
                if (prev.pariman2 && value) {
                    const total = calculateTotal(prev.pariman2, value, getDigitType(prev.pariman2));
                    updatedData.jamma_paral_mulya3 = total;
                }
                
                return updatedData;
            });
        } else if (name === 'jamma_paral_mulya' || name === 'jamma_paral_mulya2' || name === 'jamma_paral_mulya3') {
            // Handle total fields with calculation support
            const result = calculateWithMixedDigits(value, getDigitType(value));
            setLocalFormData(prev => ({
                ...prev,
                [name]: result
            }));
        } else {
            setLocalFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Excel Upload and Template Functions
    const downloadTemplate = async () => {
        try {
                    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/items/download-template/`, {
            method: 'GET',
        });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'store_management_template.xlsx';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                console.error('Failed to download template');
            }
        } catch (error) {
            console.error('Error downloading template:', error);
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setIsUploading(true);
        setUploadStatus('Uploading file...');

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/upload-excel/`, {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                const result = await response.json();
                console.log('Upload response:', result);
                setUploadStatus(`✅ Successfully uploaded ${result.success_count} items!`);
                
                // Optionally refresh the view or show success message
                setTimeout(() => {
                    setUploadStatus('');
                }, 5000);
            } else {
                const error = await response.json();
                setUploadStatus(`❌ Upload failed: ${error.error || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Upload error:', error);
            setUploadStatus('❌ Upload failed: Network error');
        } finally {
            setIsUploading(false);
            // Clear the file input
            event.target.value = '';
        }
    };

    const handleNepaliDateChange = (nepaliDate) => {
        // Convert Nepali date to English date
        const englishDate = convertNepaliToEnglish(nepaliDate);
        setLocalFormData(prev => ({
            ...prev,
            miti_nepali: nepaliDate,
            miti: englishDate
        }));
        setShowNepaliCalendar(false); // Close calendar after selection
    };

    // Proper Nepali to English date conversion with Nepali digits support
    const convertNepaliToEnglish = (nepaliDate) => {
        if (!nepaliDate) return '';
        
        // Convert Nepali digits to English digits
        const nepaliToEnglishDigits = {
            '०': '0', '१': '1', '२': '2', '३': '3', '४': '4',
            '५': '5', '६': '6', '७': '7', '८': '8', '९': '9'
        };
        
        let englishDate = nepaliDate;
        Object.keys(nepaliToEnglishDigits).forEach(nepaliDigit => {
            englishDate = englishDate.replace(new RegExp(nepaliDigit, 'g'), nepaliToEnglishDigits[nepaliDigit]);
        });
        
        // Parse Nepali date (format: yyyy-mm-dd or २०८२-०८-०७)
        const parts = englishDate.split('-');
        if (parts.length !== 3) return nepaliDate;
        
        const nepaliYear = parseInt(parts[0]);
        const nepaliMonth = parseInt(parts[1]);
        const nepaliDay = parseInt(parts[2]);
        
        // Validate the parsed values
        if (isNaN(nepaliDay) || isNaN(nepaliMonth) || isNaN(nepaliYear)) {
            return nepaliDate; // Return original if parsing failed
        }
        
        // Convert Nepali year to English year (subtract 57)
        const englishYear = nepaliYear - 57;
        
        // Format as English date (yyyy-mm-dd)
        const formattedEnglishDate = `${englishYear}-${String(nepaliMonth).padStart(2, '0')}-${String(nepaliDay).padStart(2, '0')}`;
        
        return formattedEnglishDate;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (isEditMode && onSubmit) {
            // In edit mode, call the parent's onSubmit function
            onSubmit(localFormData);
            return;
        }
        
        try {
                    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/items/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(localFormData)
            });
            
            if (response.ok) {
                alert('Data submitted successfully!');
                // Clear local storage after successful submission
                clearLocalStorage();
                setLocalFormData({
                    miti: '', miti_nepali: '', dakshila_no: '', specification: '', saman_ko_pahichan: '',
                    model_no: '', utpadan_garnedesh: '', size: '', anumit_aayu: '',
                    saman_prabidhik_srot: '', parikshan: '', prati_ekai_ko_dar: '',
                    jamma_paral_mulya: '', pariman: '', jamma_paral_mulya2: '',
                    pariman2: '', jamma_paral_mulya3: '', kaifiyat: '',
                    form_number: '', previous_form_number: '', fiscal_year: '',
                    inventory_code: '', inventory_page_number: '', item_name: '',
                    unit_value: ''
                });
            } else {
                alert('Error submitting data');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error submitting data');
        }
    };

    return (
        <div className={styles['store-form-container']}>
            <div className={styles['form-header']}>
                <h2>Store Management Form</h2>
                <div className={styles['storage-controls']}>
                    {hasUnsavedData && !isEditMode && (
                        <span className={styles['unsaved-indicator']}>
                            ⚠️ Unsaved changes (auto-saving in 2 seconds...)
                        </span>
                    )}
                    {!hasUnsavedData && !isEditMode && hasSavedDataAvailable() && (
                        <span className={styles['saved-indicator']}>
                            ✅ All changes saved to local storage
                        </span>
                    )}
                    {isEditMode && (
                        <span className={styles['edit-mode-indicator']}>
                            ✏️ Edit Mode - Auto-save disabled
                        </span>
                    )}
                    {lastSaved && !isEditMode && (
                        <span className={styles['last-saved']}>
                            💾 Last saved: {lastSaved.toLocaleTimeString()}
                        </span>
                    )}
                    <button 
                        type="button" 
                        onClick={handleRestoreClick}
                        className={styles['restore-btn']}
                        title={hasSavedDataAvailable() ? "Restore saved data" : "No saved data available"}
                        disabled={isEditMode || !hasSavedDataAvailable()}
                    >
                        ⏰ {hasSavedDataAvailable() ? 'Restore Data' : 'No Data to Restore'}
                    </button>
                    <button 
                        type="button" 
                        onClick={() => saveToLocalStorage(localFormData)}
                        className={styles['save-btn']}
                        title="Save data to local storage"
                    >
                        💾 Save Now
                    </button>
                    <button 
                        type="button" 
                        onClick={downloadTemplate}
                        className={styles['template-btn']}
                        title="Download Excel template"
                    >
                        📋 Download Template
                    </button>
                    <div className={styles['upload-section']}>
                        <input
                            type="file"
                            id="excel-upload"
                            accept=".xlsx,.xls,.csv"
                            onChange={handleFileUpload}
                            style={{ display: 'none' }}
                            disabled={isUploading}
                        />
                        <label htmlFor="excel-upload" className={`${styles['upload-btn']} ${isUploading ? styles['uploading'] : ''}`}>
                            {isUploading ? '⏳ Uploading...' : '📤 Upload Excel'}
                        </label>
                    </div>
                    {uploadStatus && (
                        <div className={`${styles['upload-status']} ${uploadStatus.includes('✅') ? styles['success'] : styles['error']}`}>
                            {uploadStatus}
                        </div>
                    )}
                </div>
            </div>
            
            {/* Official Header Section */}
            <div className={styles['official-header']}>
                <div className={styles['header-top']}>
                    <div className={styles['header-left']}>
                        <div className={styles['university-name']}>मध्यपश्चिम विश्वविद्यालय</div>
                        <div className={styles['office-name']}>केन्द्रीय कार्यालय</div>
                        <div className={styles['office-code']}>कार्यालय कोड नं.: <span className={styles['code-value']}>MWU-001</span></div>
                    </div>
                    <div className={styles['header-right']}>
                        <div className={styles['form-number']}>
                            <span>म.ले.प.फारम नंः</span>
                            <input
                                type="text"
                                name="form_number"
                                value={localFormData.form_number}
                                onChange={handleInputChange}
                                className={styles['header-input']}
                                placeholder="type here"
                            />
                        </div>
                        <div className={styles['previous-form']}>
                            <span>साबिकको फाराम नंः</span>
                            <input
                                type="text"
                                name="previous_form_number"
                                value={localFormData.previous_form_number}
                                onChange={handleInputChange}
                                className={styles['header-input']}
                                placeholder="type here"
                            />
                        </div>
                    </div>
                </div>
                
                <div className={styles['main-title']}>
                    खर्च भएर नजाने (खप्ने मालसामानको) जिन्सी खाता
                </div>
                
                <div className={styles['sub-header']}>
                    <div className={styles['fiscal-year']}>
                        <span>आर्थिक वर्षः</span>
                        <input
                            type="text"
                            name="fiscal_year"
                            value={localFormData.fiscal_year}
                            onChange={handleInputChange}
                            className={styles['header-input']}
                            placeholder="type here"
                        />
                    </div>
                    <div className={styles['inventory-info']}>
                        <span>
                            <span>जिन्सी सङ्केत नंः</span>
                            <input
                                type="text"
                                name="inventory_code"
                                value={localFormData.inventory_code}
                                onChange={handleInputChange}
                                className={styles['header-input']}
                                placeholder="Enter code"
                            />
                        </span>
                        <span>
                            <span>जिन्सी खाता पाना नंः</span>
                            <input
                                type="text"
                                name="inventory_page_number"
                                value={localFormData.inventory_page_number}
                                onChange={handleInputChange}
                                className={styles['header-input']}
                                placeholder="type here"
                            />
                        </span>
                    </div>
                </div>
            </div>
            
            {/* Table Header Section */}
            <div className={styles['table-header']}>
                <div className={styles['table-row']}>
                    <div className={styles['table-cell']}>
                        <span className={styles['nepali-text']}>जिन्सी सामानको नाम:</span>
                    </div>
                    <div className={styles['table-cell']}>
                        <input
                            type="text"
                            name="item_name"
                            value={localFormData.item_name}
                            onChange={handleInputChange}
                            className={styles['table-input']}
                            placeholder="type here"
                        />
                    </div>
                </div>
                <div className={styles['table-row']}>
                    <div className={styles['table-cell']}>
                        <span className={styles['nepali-text']}>इकाई:</span>
                    </div>
                    <div className={styles['table-cell']}>
                        <input
                            type="text"
                            name="unit_value"
                            value={localFormData.unit_value}
                            onChange={handleInputChange}
                            className={styles['table-input']}
                            placeholder="थान"
                        />
                    </div>
                </div>
            </div>
            
            <form onSubmit={handleSubmit} className={styles['store-form']}>
                <div className={styles['form-grid']}>
                    <div className={styles['form-group']}>
                        <label>1. मिति (Date) - Nepali</label>
                        <input
                            type="text"
                            name="miti_nepali"
                            value={localFormData.miti_nepali}
                            onChange={handleInputChange}
                            placeholder="yyyy-mm-dd (e.g., २०८२-०८-०७)"
                            required
                        />
                        <button 
                            type="button" 
                            onClick={() => setShowNepaliCalendar(!showNepaliCalendar)}
                            className={`${styles['calendar-btn']} calendar-btn`}
                        >
                            📅
                        </button>
                        {showNepaliCalendar && (
                            <div className={`${styles['calendar-popup']} calendar-popup`}>
                                <DatePicker
                                    value={localFormData.miti_nepali}
                                    onChange={handleNepaliDateChange}
                                    type="nepali"
                                    placeholder="Select Nepali date"
                                />
                            </div>
                        )}
                    </div>

                    <div className={styles['form-group']}>
                        <label>मिति (Date) - English</label>
                        <input
                            type="text"
                            name="miti"
                            value={localFormData.miti}
                            onChange={handleInputChange}
                            placeholder="Auto-converted (yyyy-mm-dd format)"
                            readOnly
                            className={styles['readonly-input']}
                        />
                    </div>

                    <div className={styles['form-group']}>
                        <label>2. दाखिला न/ हस्तान्तरण न. (Entry/Transfer No.)</label>
                        <input
                            type="text"
                            name="dakshila_no"
                            value={localFormData.dakshila_no}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className={styles['form-group']}>
                        <label>3. स्पेसिफिकेशन (Specification)</label>
                        <input
                            type="text"
                            name="specification"
                            value={localFormData.specification}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className={styles['form-group']}>
                        <label>4. सामानको पहिचान न (Item Identification No.)</label>
                        <input
                            type="text"
                            name="saman_ko_pahichan"
                            value={localFormData.saman_ko_pahichan}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className={styles['form-group']}>
                        <label>5. मोडेल नं (Model No.)</label>
                        <input
                            type="text"
                            name="model_no"
                            value={localFormData.model_no}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className={styles['form-group']}>
                        <label>6. उत्पादन गर्ने देश वा कम्पनि/को नाम (Manufacturing Country/Company)</label>
                        <input
                            type="text"
                            name="utpadan_garnedesh"
                            value={localFormData.utpadan_garnedesh}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className={styles['form-group']}>
                        <label>7. साइज़ (Size)</label>
                        <input
                            type="text"
                            name="size"
                            value={localFormData.size}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className={styles['form-group']}>
                        <label>8. अनुमानित आयु (Estimated Age)</label>
                        <input
                            type="text"
                            name="anumit_aayu"
                            value={localFormData.anumit_aayu}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className={styles['form-group']}>
                        <label>9. सामान प्राप्ति स्रोत (Item Acquisition Source)</label>
                        <input
                            type="text"
                            name="saman_prabidhik_srot"
                            value={localFormData.saman_prabidhik_srot}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className={styles['form-group']}>
                        <label>10. परिणाम (Quantity)</label>
                        <input
                            type="text"
                            name="parikshan"
                            value={localFormData.parikshan}
                            onChange={handleInputChange}
                            placeholder="Enter quantity (e.g., 10 or १०)"
                            required
                        />
                        <small className={styles['input-hint']}>
                            💡 You can use English (1,2,3) or Nepali (१,२,३) digits
                        </small>
                    </div>

                    <div className={styles['form-group']}>
                        <label>11. प्रति एकाइको दर (Rate per Unit)</label>
                        <input
                            type="text"
                            name="prati_ekai_ko_dar"
                            value={localFormData.prati_ekai_ko_dar}
                            onChange={handleInputChange}
                            placeholder="Enter rate (e.g., 100.50 or १००.५०)"
                            required
                        />
                        <small className={styles['input-hint']}>
                            💡 Auto-calculates totals when quantity is entered
                        </small>
                    </div>

                    <div className={styles['form-group']}>
                        <label>12. जम्मा परल मूल्य (Total Package Value)</label>
                        <input
                            type="text"
                            name="jamma_paral_mulya"
                            value={localFormData.jamma_paral_mulya}
                            onChange={handleInputChange}
                            placeholder="Auto-calculated or enter manually (e.g., 1000 or १०००)"
                            required
                        />
                        <small className={styles['input-hint']}>
                            💡 Supports calculations like "10*100" or "१०×१००"
                        </small>
                    </div>

                    <div className={styles['form-group']}>
                        <label>13. परिमाण (Quantity)</label>
                        <input
                            type="text"
                            name="pariman"
                            value={localFormData.pariman}
                            onChange={handleInputChange}
                            placeholder="Enter quantity (e.g., 5 or ५)"
                            required
                        />
                        <small className={styles['input-hint']}>
                            💡 You can use English (1,2,3) or Nepali (१,२,३) digits
                        </small>
                    </div>

                    <div className={styles['form-group']}>
                        <label>14. जम्मा परल मूल्य (Total Package Value)</label>
                        <input
                            type="text"
                            name="jamma_paral_mulya2"
                            value={localFormData.jamma_paral_mulya2}
                            onChange={handleInputChange}
                            placeholder="Auto-calculated or enter manually (e.g., 500 or ५००)"
                            required
                        />
                        <small className={styles['input-hint']}>
                            💡 Supports calculations like "5*100" or "५×१००"
                        </small>
                    </div>

                    <div className={styles['form-group']}>
                        <label>15. परिमाण (Quantity)</label>
                        <input
                            type="text"
                            name="pariman2"
                            value={localFormData.pariman2}
                            onChange={handleInputChange}
                            placeholder="Enter quantity (e.g., 3 or ३)"
                            required
                        />
                        <small className={styles['input-hint']}>
                            💡 You can use English (1,2,3) or Nepali (१,२,३) digits
                        </small>
                    </div>

                    <div className={styles['form-group']}>
                        <label>16. जम्मा परल मूल्य (Total Package Value)</label>
                        <input
                            type="text"
                            name="jamma_paral_mulya3"
                            value={localFormData.jamma_paral_mulya3}
                            onChange={handleInputChange}
                            placeholder="Auto-calculated or enter manually (e.g., 300 or ३००)"
                            required
                        />
                        <small className={styles['input-hint']}>
                            💡 Supports calculations like "3*100" or "३×१००"
                        </small>
                    </div>

                    <div className={`${styles['form-group']} ${styles['full-width']}`}>
                        <label>17. कैफियत (Remarks)</label>
                        <textarea
                            name="kaifiyat"
                            value={localFormData.kaifiyat}
                            onChange={handleInputChange}
                            rows="3"
                        />
                    </div>
                </div>

                <div className={styles['form-actions']}>
                    <button type="submit" className={styles['submit-btn']}>
                        {isEditMode ? 'Update Data' : 'Submit Data'}
                    </button>
                    {!isEditMode && (
                        <button type="button" className={styles['reset-btn']} onClick={() => setLocalFormData({
                            miti: '', miti_nepali: '', dakshila_no: '', specification: '', saman_ko_pahichan: '',
                            model_no: '', utpadan_garnedesh: '', size: '', anumit_aayu: '',
                            saman_prabidhik_srot: '', parikshan: '', prati_ekai_ko_dar: '',
                            jamma_paral_mulya: '', pariman: '', jamma_paral_mulya2: '',
                            pariman2: '', jamma_paral_mulya3: '', kaifiyat: '',
                            form_number: '', previous_form_number: '', fiscal_year: '',
                            inventory_code: '', inventory_page_number: '', item_name: '',
                            unit_value: ''
                        })}>
                            Reset Form
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default StoreForm;
